import React, { FunctionComponent, memo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { usePost } from "../../api-client/usePost";
import { HashLink } from "../../navigation/HashLink";
import { useDealerEnableCustomerLogin } from "../../stores/hooks/useDealerEnableCustomerLogin";
import { useNullableDealerAccountInfo } from "../../stores/hooks/useNullableDealerAccountInfo";
import { useSetInfoToast } from "../../stores/hooks/useSetInfoToast";
import { HashtagString, useHashState } from "../../stores/useHashState";
import { Button, PrimaryButton } from "../../ui/Button/Button";
import { Dialog } from "../../ui/Dialog/Dialog";
import { DialogHeader } from "../../ui/Dialog/DialogHeader";
import { Input } from "../../ui/Input/Input";
import { useForm } from "../../util/useForm";
import { signInHash } from "./SignInDialog";

export const signUpHash: HashtagString = "#sign-up";

const schema = z
  .object({
    email: z.string().nonempty({ message: "form.error.customerEmail" }),
    firstName: z.string().nonempty({ message: "form.error.customerFirstName" }),
    lastName: z.string().nonempty({ message: "form.error.customerLastName" }),
    password: z.string().nonempty({ message: "form.error.password" }),
    passwordConfirm: z.string(),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: "form.error.passwordMismatch",
    path: ["passwordConfirm"],
  });

const ToBeCached: FunctionComponent = () => {
  const { t } = useTranslation();
  const [visible, { hide }] = useHashState(signUpHash);
  const [, { show: showSignInModal }] = useHashState(signInHash);
  const dealer = useNullableDealerAccountInfo();
  const { send } = usePost(
    "/retailSiteWebService/authentication/createCustomerUserAccount.php"
  );
  const setInfoToast = useSetInfoToast();

  const [handleForm, { busy }] = useForm({
    async onSubmit({ email, firstName, lastName, password, passwordConfirm }) {
      await send({
        dealerId: dealer?.dealerId ?? "",
        dealerUrlCode: dealer?.dealerUrlCode ?? "",
        email,
        firstName,
        lastName,
        password,
        passwordConfirm,
        userIpAddress: window.ipAddress,
      });
      setInfoToast({
        action: {
          callback: showSignInModal,
          label: t("auth.signIn"),
        },
        message: t("auth.accountCreated"),
      });
    },
    schema,
  });

  const dealerEnableCustomerLogin = useDealerEnableCustomerLogin();
  if (!dealerEnableCustomerLogin) {
    return null;
  }
  return (
    <Dialog
      className="md:p-4 lg:rounded-panel md:w-full max-w-[29rem]"
      id="sign-up-dialog"
      onClose={hide}
      visible={visible}
    >
      <DialogHeader
        headingClassName="text-xl md:text-3xl font-medium"
        onClose={hide}
        title={t("auth.createAccount")}
      />
      <form className="grid gap-y-4 px-4 mt-4" noValidate onSubmit={handleForm}>
        <fieldset className="grid gap-2">
          <label
            className="text-xs text-ink-2 font-thin"
            htmlFor="sign-up-first-name"
          >
            {t("form.label.firstName")}
          </label>
          <Input
            aria-label="yourFirstName"
            autoComplete="given-name"
            autoCorrect="off"
            id="sign-up-first-name"
            name="firstName"
            placeholder={t("form.placeholder.firstName")}
            type="text"
          />
        </fieldset>
        <fieldset className="grid gap-2">
          <label
            className="text-xs text-ink-2 font-thin"
            htmlFor="sign-up-last-name"
          >
            {t("form.label.lastName")}
          </label>
          <Input
            aria-label="yourLastName"
            autoComplete="family-name"
            autoCorrect="off"
            id="sign-up-last-name"
            name="lastName"
            placeholder={t("form.placeholder.lastName")}
            type="text"
          />
        </fieldset>
        <fieldset className="grid gap-2">
          <label
            className="text-xs text-ink-2 font-thin"
            htmlFor="sign-up-email"
          >
            {t("form.label.email")}
          </label>
          <Input
            aria-label="yourEmail"
            autoCapitalize="off"
            autoComplete="email"
            autoCorrect="off"
            id="sign-up-email"
            inputMode="email"
            name="email"
            placeholder={t("form.placeholder.email")}
            type="text"
          />
        </fieldset>
        <fieldset className="grid gap-2">
          <label
            className="text-xs text-ink-2 font-thin"
            htmlFor="sign-up-password"
          >
            {t("form.label.password")}
          </label>
          <Input
            aria-label="yourPassword"
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            id="sign-up-password"
            name="password"
            type="text"
          />
        </fieldset>
        <fieldset className="grid gap-2">
          <label
            className="text-xs text-ink-2 font-thin"
            htmlFor="sign-up-password-confirm"
          >
            {t("form.label.passwordConfirm")}
          </label>
          <Input
            aria-label="yourPasswordConfirm"
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            id="sign-up-password-confirm"
            name="passwordConfirm"
            type="text"
          />
        </fieldset>
        <div className="grid grid-cols-2 gap-2 pb-6 mt-2">
          <Button
            aria-label="closeSignUpDialog"
            className="text-ink-2 underline font-thin h-14"
            element={HashLink}
            hash={signInHash}
          >
            {t("auth.cancel")}
          </Button>
          <PrimaryButton
            aria-label="submitSignupForm"
            busy={busy}
            type="submit"
          >
            {t("auth.signUp")}
          </PrimaryButton>
        </div>
      </form>
    </Dialog>
  );
};

export const SignupDialog = memo(ToBeCached);
